import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IJourneyResp, ISchedules } from '../../services/journey.service';
import { EnumJourneyAction, getJourneyAction } from '../../utils/enum/journeyAction';
import { getCurrentSchedule } from '../../components/shared/JourneyActionManager';

export interface Calendar {
  id: string;
  type: string;
  durationEvent: number;
  alias: string;
  inviteFisicalAddress: string;
}
export enum ECalendarType {
  'MAIN' = 'MAIN',
  'RETURN' = 'RETURN',
  'BOTH' = 'BOTH'
}
export interface ICalaendarType {
  calendarId: string;
  createdAt: string;
  id: string;
  productId: string;
  type: ECalendarType;
  updatedAt: string;
}
export enum EScheduleType {
  'DEFAULT' = 'DEFAULT',
  'RETURN' = 'RETURN'
}

export enum TypeOfAppointment {
  PHYSICAL_APPOINTMENT = 'PHYSICAL_APPOINTMENT',
  VIRTUAL_CONSULTATION = 'VIRTUAL_CONSULTATION',
  EXAM = 'EXAM',
  PROCEDURE = 'PROCEDURE'
}
export interface CalendarAvailables {
  calendarsAvailables: Calendar[];
  calendarSelected: Calendar | null;
  journey: IJourneyResp;
  doctorId?: string | null;
  withTheSameDoctor?: boolean;
  typeOfAppointment?: TypeOfAppointment | null;
  isReSchedule?: boolean | null;
  schedule?: ISchedules | null;
}

export interface SaveAvailableCalendarsDTO {
  journey: IJourneyResp;
  scheduleType: EScheduleType;
  schedule?: ISchedules;
}

export interface ChooseCalendarResult {
  availableList: Calendar[];
  journey: IJourneyResp;
  doctorId?: string | null;
  calendarSelected: Calendar | null;
}

export const chooseCalendar = createAsyncThunk('calendar/chooseCalendar', async (payload: SaveAvailableCalendarsDTO) => {
  const {
    journey,
    journey: { calendarSecundary },
    scheduleType,
    schedule
  } = payload;

  const status = getJourneyAction(journey);
  const currentSchedule = schedule ? schedule : (getCurrentSchedule(journey) as ISchedules);
  const filterByType =
    scheduleType === EScheduleType.DEFAULT
      ? [ECalendarType.MAIN, ECalendarType.BOTH]
      : [ECalendarType.RETURN, ECalendarType.BOTH];
  const calendarTypes = journey.calendarTypes.filter(type => filterByType.includes(type.type));

  const getAvailableList = (): Calendar[] => {
    const scheduleCalendar = journey.calendars?.find(calendar => calendar.id === currentSchedule.calendarId);
    //Agendamento de consulta pos entrega dois exames
    if (status === EnumJourneyAction.AWAITING_SCHEDULE && calendarSecundary) {
      return [calendarSecundary];
    }

    //Reagendamento de Exame
    if (scheduleCalendar?.type === 'EXAM') {
      return [scheduleCalendar] as Calendar[];
    }

    //Reagendamento de consulta pos entrega dois exames
    if (calendarSecundary) {
      return [calendarSecundary];
    }
    //Todos os outros casos
    return journey.calendars?.filter(calendar => calendarTypes.some(type => type.calendarId === calendar.id)) as Calendar[];
  };

  const availableList = getAvailableList();
  return {
    availableList,
    journey,
    doctorId: schedule?.user.id,
    calendarSelected: availableList?.length === 1 ? availableList[0] : null
  };
});

export const calendarSlice = createSlice({
  name: 'calendars',
  initialState: {
    calendarsAvailables: [] as Calendar[],
    calendarSelected: null
  } as CalendarAvailables,
  reducers: {
    /**
     * @deprecated
     */
    saveAvailableCalendars: (state, action: PayloadAction<SaveAvailableCalendarsDTO>) => {
      const {
        journey,
        journey: { calendarSecundary },
        scheduleType,
        schedule
      } = action.payload;

      const status = getJourneyAction(journey);
      const currentSchedule = schedule ? schedule : (getCurrentSchedule(journey) as ISchedules);
      const filterByType =
        scheduleType === EScheduleType.DEFAULT
          ? [ECalendarType.MAIN, ECalendarType.BOTH]
          : [ECalendarType.RETURN, ECalendarType.BOTH];
      const calendarTypes = journey.calendarTypes.filter(type => filterByType.includes(type.type));

      const getAvailableList = (): Calendar[] => {
        //Reagendamento de Exame
        if (status === EnumJourneyAction.AWAITING_EXAM) {
          const currentScheduleCalendarId = currentSchedule.calendarId;
          const currentScheduleCalendar = journey.calendars?.filter(
            calendar => calendar.id === currentScheduleCalendarId
          )[0];
          return [currentScheduleCalendar] as Calendar[];
        }
        //Agendamento de consulta pos entrega dois exames
        if (status === EnumJourneyAction.AWAITING_SCHEDULE && calendarSecundary) {
          return [calendarSecundary];
        }
        //Reagendamento de consulta pos entrega dois exames
        if (calendarSecundary) {
          return [calendarSecundary];
        }
        //Todos os outros casos
        return journey.calendars?.filter(calendar =>
          calendarTypes.some(type => type.calendarId === calendar.id)
        ) as Calendar[];
      };

      const availableList = getAvailableList();
      state.calendarsAvailables = availableList || ([] as Calendar[]);
      state.journey = journey;
      state.doctorId = journey.doctor?.id;
      if (availableList?.length === 1) {
        state.calendarSelected = availableList[0];
      } else {
        state.calendarSelected = null;
      }
    },
    saveCalendarSelected: (state, action: PayloadAction<{ calendar: Calendar | null } | null>) => {
      const { calendar } = action.payload || { calendar: null };
      state.calendarSelected = calendar;
    },

    prepareToCalendarSelected: (state, action: PayloadAction<{ isReSchedule: boolean | null | undefined }>) => {
      state.calendarSelected = null;
      state.typeOfAppointment = null;
      state.isReSchedule = action.payload.isReSchedule;
    },

    prepareToSelectedPhysicalAppointmentsWithMutipleAddress: (
      state,
      action: PayloadAction<{ isReSchedule: boolean | null | undefined }>
    ) => {
      state.calendarSelected = null;
      state.isReSchedule = action.payload.isReSchedule;
    },

    changeExameAppointment: (state, action: PayloadAction<{ isReSchedule: boolean | null | undefined }>) => {
      state.isReSchedule = action.payload.isReSchedule;
    },

    changeScheduleAppointment: (state, action: PayloadAction<{ isReSchedule: boolean | null | undefined }>) => {
      state.isReSchedule = action.payload.isReSchedule;
    },

    changeRescheduleAppointment: (state, action: PayloadAction<{ isReSchedule: boolean | null | undefined }>) => {
      state.isReSchedule = action.payload.isReSchedule;
    },

    clearCalendarSelected: state => {
      state.calendarSelected = null;
      state.typeOfAppointment = null;
      state.isReSchedule = null;
      state.schedule = null;
    },
    resetCalendarsState: state => {
      state.calendarsAvailables = [];
      state.calendarSelected = null;
      state.doctorId = null;
      state.typeOfAppointment = null;
      state.isReSchedule = null;
      state.schedule = null;
      state.journey = {} as IJourneyResp;
    },
    withTheSameDoctor: (state, action: PayloadAction<boolean>) => {
      state.withTheSameDoctor = action.payload;
    },
    isReSchedule: (state, action: PayloadAction<boolean>) => {
      state.isReSchedule = action.payload;
    },
    saveTypeOfAppointment: (state, action: PayloadAction<{ typeOfAppointment: TypeOfAppointment }>) => {
      state.typeOfAppointment = action.payload.typeOfAppointment;
    },
    saveCurrentSchedule: (state, action: PayloadAction<ISchedules>) => {
      state.schedule = action.payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(chooseCalendar.fulfilled, (state, action) => {
      const { availableList, journey, doctorId, calendarSelected } = action.payload;
      state.calendarsAvailables = availableList || ([] as Calendar[]);
      state.journey = journey;
      state.doctorId = doctorId;
      state.calendarSelected = calendarSelected;
    });
  }
});

export const {
  saveAvailableCalendars,
  saveCalendarSelected,
  clearCalendarSelected,
  resetCalendarsState,
  withTheSameDoctor,
  isReSchedule,
  saveTypeOfAppointment,
  prepareToCalendarSelected,
  prepareToSelectedPhysicalAppointmentsWithMutipleAddress,
  changeExameAppointment,
  changeScheduleAppointment,
  changeRescheduleAppointment,
  saveCurrentSchedule
} = calendarSlice.actions;
export const calendarReducer = calendarSlice.reducer;
